// DependencyContainer.ts
import { ILoggerService } from './Services/Logging/ILoggerService';
import { LoggerService } from './Services/Logging/LoggerService';
import { TypedEvent } from './TypedEvent';
import { CancelationTokenSource } from './CancelationTokenSource';
import { IDIService } from './IDIService';

export class DependencyContainer {
    private constructor() { }

    private _services = new Map<string, IDIService>();
    private _serviceRegistrationEvent = new TypedEvent<IDIService>();
    private _pendingResolutions = new Map<number, PendingServiceResolution>();
    private _nextResolutionId = 1;

    public static readonly ServiceName = "DependencyContainer";
    public static Log?: ILoggerService;
    public static IsInitialized = false;

    private static get Timers(): Pick<typeof globalThis, "setTimeout" | "clearTimeout"> {
        return globalThis;
    }

    public static get Instance(): DependencyContainer {
        const g = globalThis as any;

        if (g.__dc_instance)
            return g.__dc_instance;

        const inst = new DependencyContainer();
        g.__dc_instance = inst;

        if (typeof window !== 'undefined' && !(window as any).LavvaDI)
            (window as any).appservices = inst;

        this.Log = new LoggerService();
        this.Log.Warning(DependencyContainer.ServiceName, 'Initializing DependencyContainer');

        inst.RegisterService(this.Log);
        this.IsInitialized = true;
        return inst;
    }

    public RegisterService(srv: IDIService): void {
        const serviceName = srv.GetServiceName();

        if (this._services.has(serviceName)) {
            DependencyContainer.Log?.Warning(
                DependencyContainer.ServiceName,
                `Service "${serviceName}" is being re-registered and the previous instance will be overwritten.`
            );
        }

        this._services.set(serviceName, srv);
        this._serviceRegistrationEvent.Invoke(srv);
    }

    public GetService<T extends IDIService>(name: string): T {
        return this._services.get(name) as unknown as T;
    }

    public GetServiceAsync<T extends IDIService>(name: string, token?: CancelationTokenSource): Promise<T> {
        if (this._services.has(name))
            return Promise.resolve(this._services.get(name) as unknown as T);

        const resolution = this.CreatePendingResolution(name);
        this._pendingResolutions.set(resolution.Id, resolution);
        DependencyContainer.Log?.Debug(
            DependencyContainer.ServiceName,
            `Waiting for service "${name}" (request #${resolution.Id}). Registered services: ${this.GetRegisteredServiceNamesForLogs()}.`
        );

        return new Promise<T>((resolve, reject) => {
            const h = (s: IDIService) => {
                if (s.GetServiceName() === name && this.TryCompleteResolution(resolution.Id)) {
                    this._serviceRegistrationEvent.Unsubscribe(h);
                    token?.CancellationEvent.Unsubscribe(onCancel);
                    const waitedMs = Date.now() - resolution.StartedAt;
                    DependencyContainer.Log?.Info(
                        DependencyContainer.ServiceName,
                        `Resolved service "${name}" after ${waitedMs}ms (request #${resolution.Id}).`
                    );
                    resolve(s as unknown as T);
                }
            };
            const onCancel = () => {
                this._serviceRegistrationEvent.Unsubscribe(h);
                if (!this.TryCompleteResolution(resolution.Id))
                    return;

                const waitedMs = Date.now() - resolution.StartedAt;
                DependencyContainer.Log?.Warning(
                    DependencyContainer.ServiceName,
                    `Cancelled waiting for service "${name}" after ${waitedMs}ms (request #${resolution.Id}).`
                );
                reject(new Error('cancelled'));
            };
            token?.CancellationEvent.Subscribe(onCancel);
            this._serviceRegistrationEvent.Subscribe(h);
        });
    }

    public GetServiceWithTimeoutAsync<T extends IDIService>(name: string, timeout: number): Promise<T> {
        if (this._services.has(name))
            return Promise.resolve(this._services.get(name) as unknown as T);

        const resolution = this.CreatePendingResolution(name);
        this._pendingResolutions.set(resolution.Id, resolution);
        DependencyContainer.Log?.Debug(
            DependencyContainer.ServiceName,
            `Waiting for service "${name}" with timeout ${timeout}ms (request #${resolution.Id}). Registered services: ${this.GetRegisteredServiceNamesForLogs()}.`
        );

        return new Promise<T>((resolve, reject) => {
            const h = (s: IDIService) => {
                if (s.GetServiceName() === name && this.TryCompleteResolution(resolution.Id)) {
                    DependencyContainer.Timers.clearTimeout(tid);
                    this._serviceRegistrationEvent.Unsubscribe(h);
                    const waitedMs = Date.now() - resolution.StartedAt;
                    DependencyContainer.Log?.Info(
                        DependencyContainer.ServiceName,
                        `Resolved service "${name}" after ${waitedMs}ms (request #${resolution.Id}).`
                    );
                    resolve(s as unknown as T);
                }
            };
            const tid = DependencyContainer.Timers.setTimeout(() => {
                this._serviceRegistrationEvent.Unsubscribe(h);
                if (!this.TryCompleteResolution(resolution.Id))
                    return;

                const waitedMs = Date.now() - resolution.StartedAt;
                DependencyContainer.Log?.Warning(
                    DependencyContainer.ServiceName,
                    `Timeout waiting for service "${name}" after ${waitedMs}ms (request #${resolution.Id}). Registered services: ${this.GetRegisteredServiceNamesForLogs()}.`
                );
                reject(new Error(`timeout waiting for "${name}"`));
            }, timeout);
            this._serviceRegistrationEvent.Subscribe(h);
        });
    }

    private CreatePendingResolution(name: string): PendingServiceResolution {
        return {
            Id: this._nextResolutionId++,
            ServiceName: name,
            StartedAt: Date.now(),
        };
    }

    private TryCompleteResolution(id: number): boolean {
        return this._pendingResolutions.delete(id);
    }

    private GetRegisteredServiceNamesForLogs(): string {
        const names = [...this._services.keys()].sort();

        if (names.length === 0)
            return "<none>";

        return names.join(", ");
    }
}

interface PendingServiceResolution {
    Id: number;
    ServiceName: string;
    StartedAt: number;
}
